import {
  type ExoSkeletonEventNames,
  EXO_SKELETON_RUNTIME_SCOPE,
} from './consts';
import { createUniqueRequestId } from './internal';

export interface ExoSkeletonRuntimeDataEvent<
  TYPE extends ExoSkeletonEventNames,
  DATA,
> {
  requestId: string;
  type: TYPE;
  data: DATA;
  eventTimestampUTC: number;
}

export function createRuntimeDataEvent<
  TYPE extends ExoSkeletonEventNames,
  DATA,
>(type: TYPE, data: DATA): ExoSkeletonRuntimeDataEvent<TYPE, DATA> {
  return {
    requestId: createUniqueRequestId(type),
    type,
    data,
    eventTimestampUTC: new Date(new Date().toUTCString()).getTime(),
  };
}

export function isInExoSkeleton() {
  return Boolean(window[EXO_SKELETON_RUNTIME_SCOPE]?.available);
}

export function createPostMessageEventHandler<T>(
  callback: ({ data }: MessageEvent<T>) => void,
) {
  window.addEventListener('message', callback);

  return {
    dispose() {
      window.removeEventListener('message', callback);
    },
  };
}

export function sendRuntimeDataEvent<TYPE extends ExoSkeletonEventNames, DATA>(
  type: TYPE,
  data: DATA,
): Promise<ExoSkeletonRuntimeDataEvent<TYPE, DATA>> {
  const targetOrigin = window.location.origin;

  const event = createRuntimeDataEvent(type, data);

  return new Promise<typeof event>((resolve) => {
    const listenerCallback: ({ data }: MessageEvent<typeof event>) => void = ({
      data: eventData,
    }) => {
      if (eventData.requestId === event.requestId) {
        resolve(eventData);
        window.removeEventListener('message', listenerCallback);
      }
    };

    window.addEventListener('message', listenerCallback);
    try {
      window.postMessage(event, targetOrigin);
    } catch (e) {
      const originalEventData = event.data;
      event.data = JSON.parse(JSON.stringify(event.data));

      window.postMessage(event, targetOrigin);

      console.error(
        'Failed to send parse "postMessage" data, fallback to "JSON.parse(JSON.stringify(...))"',
        e,
        event,
        originalEventData,
      );
    }
  });
}
